import { useEffect, useRef, useState } from 'react';

import { ArrowDown } from '@/components/ui/Icons';
import { Flex } from '@/new_src/view/components/Flex';
import { Text } from '@/new_src/view/components/Text';
import { translate } from '@/utils/LocaleUtil';

import styled from 'styled-components';
import { useTheme } from 'styled-components';

type RowCountProps = {
  pageSize: number;
  onPageSizeChange(size: number): void;
  options?: number[];
};

const DEFAULT_OPTIONS = [10, 30, 50, 100];

export const RowCount = ({ pageSize, onPageSizeChange, options = DEFAULT_OPTIONS }: RowCountProps) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const dropdownHeight = 200; // max-height of DropdownMenu
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // 아래쪽 공간이 부족하고 위쪽 공간이 더 많으면 위로 열기
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    };

    updatePosition();
  }, [isOpen]);

  /**
   * 드롭다운이 열린 동안 외부 클릭과 Escape 키 입력으로 드롭다운을 닫는다.
   * 화면 전체를 덮는 backdrop을 쓰지 않아 목록 영역의 스크롤 이벤트를 막지 않는다.
   */
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const container = containerRef.current;

      if (!container || !(event.target instanceof Node)) return;
      if (container.contains(event.target)) return;

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (value: number) => {
    onPageSizeChange(value);
    setIsOpen(false);
  };

  return (
    <Flex align="center" gap="2px">
      <SelectContainer ref={containerRef}>
        <SelectButton type="button" onClick={() => setIsOpen(!isOpen)}>
          <SelectValue>{pageSize}</SelectValue>
          <ArrowIcon $isOpen={isOpen}>
            <ArrowDown width={12} height={12} fill={theme.mantineColors.gray[8]} />
          </ArrowIcon>
        </SelectButton>
        {isOpen && (
          <>
            {/* TODO: Dropdown 컴포넌트로 추후 수정 필요
             ** https://www.figma.com/design/t8VINTS1iMOuGc9YmmFyxI/-JANGBOO--%EC%8A%A4%ED%8C%AC%EB%94%A7-UI-%EB%9D%BC%EC%9D%B4%EB%B8%8C%EB%9F%AC%EB%A6%AC?node-id=8458-161809&t=IAiD8Ah56jDTrmVo-4
             */}
            <DropdownMenu position={dropdownPosition}>
              {options.map(option => (
                <DropdownItem key={option} $selected={option === pageSize} onClick={() => handleSelect(option)}>
                  <Text
                    size="14px"
                    lh="160%"
                    lts="-0.07px"
                    fw={400}
                    c={option === pageSize ? 'blue.6' : 'gray.8'}
                    truncate={false}
                  >
                    {option}
                  </Text>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </>
        )}
      </SelectContainer>
      <Text size="12px" lh="160%" lts="-0.06px" c="gray.7">
        {translate('items')}
      </Text>
    </Flex>
  );
};

const SelectContainer = styled.div`
  position: relative;
  width: 56px;
`;

const SelectButton = styled.button`
  width: 56px;
  height: 28px;
  padding: 4px 8px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.mantineColors.gray[4]};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.mantineColors.gray[1]};
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.mantineColors.gray[8]};
  cursor: pointer;
  outline: none;

  &:hover {
    border-color: ${({ theme }) => theme.mantineColors.gray[5]};
  }

  &:focus {
    border-color: ${({ theme }) => theme.mantineColors.blue[6]};
  }
`;

const SelectValue = styled.span`
  flex: 1;
  text-align: left;
  line-height: 160%;
  letter-spacing: -0.06px;
`;

const ArrowIcon = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $isOpen }) => ($isOpen ? 'scaleY(-1)' : 'scaleY(1)')};
  transition: transform 0.1s ease;
`;

const DropdownMenu = styled.div<{ position: 'top' | 'bottom' }>`
  position: absolute;
  ${({ position }) =>
    position === 'top'
      ? `
    bottom: calc(100% + 4px);
  `
      : `
    top: calc(100% + 4px);
  `}
  left: 0;
  width: 65px;
  max-height: 200px;
  padding: 8px 0;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${({ theme }) => theme.mantineColors.gray[1]};
  border: 1px solid ${({ theme }) => theme.mantineColors.gray[4]};
  border-radius: 4px;
  box-shadow: 0px 4px 12px 0px ${({ theme }) => theme.createRGBAWithHex(theme.mantineColors.gray[8], 0.15)};
  z-index: 1000;
  pointer-events: auto;
`;

const DropdownItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  width: 25px;
  height: 22px;
  margin: 0 8px;
  padding: 8px 12px;
  box-sizing: content-box;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.mantineColors.blue[1] : theme.mantineColors.gray[1]};

  &:hover {
    background-color: ${({ $selected, theme }) =>
      $selected ? theme.mantineColors.blue[1] : theme.mantineColors.gray[2]};
  }

  &:active {
    background-color: ${({ $selected, theme }) =>
      $selected ? theme.mantineColors.blue[1] : theme.mantineColors.gray[3]};
  }
`;
